import {
  Color,
  DataTexture,
  MeshToonMaterial,
  NearestFilter,
  RedFormat,
} from 'three';

/**
 * Cel-shading factory.
 *
 * MeshToonMaterial uses a 1D gradient ramp texture to "step" lighting into N
 * discrete bands instead of the smooth gradient of MeshStandardMaterial. We
 * generate a 3-tone ramp (shadow / mid / lit) once at module load and reuse
 * it across all materials — one ramp = one matched look across body + items.
 *
 * The ramp is a 1×4 RedFormat texture with NearestFilter so transitions are
 * crisp single-pixel bands, exactly like the splash-art reference.
 */

let cachedRamp: DataTexture | null = null;

/**
 * Five-stop toon ramp: deep-shadow / shadow / mid / lit / hot. The previous
 * 2-stop ramp produced harsh blocky transitions on the body and equipment;
 * five stops keep a clear cel-look while smoothing the value bands so each
 * surface reads as a soft gradient rather than two flat patches glued together.
 */
export function getToonRamp(): DataTexture {
  if (cachedRamp) return cachedRamp;
  // 5 stops × 1px × R-only. Each stop is one pixel — NearestFilter snaps the
  // colour into the closest band, so we still get crisp cel boundaries (no
  // blurry gradient) but with 4 transitions instead of 1.
  const data = new Uint8Array([0x35, 0x68, 0x96, 0xc8, 0xff]);
  const tex = new DataTexture(data, 5, 1, RedFormat);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.needsUpdate = true;
  cachedRamp = tex;
  return tex;
}

export interface ToonMaterialOpts {
  color?: Color | string | number;
  emissive?: Color | string | number;
  emissiveIntensity?: number;
  /** If the original material was transparent (e.g. hair planes). */
  transparent?: boolean;
}

/**
 * Build a fresh MeshToonMaterial with the shared gradient ramp.
 * Each call returns a new material — caller owns it and disposes if needed.
 */
export function makeToonMaterial(opts: ToonMaterialOpts = {}): MeshToonMaterial {
  const m = new MeshToonMaterial({
    color: opts.color ?? '#ffffff',
    gradientMap: getToonRamp(),
    transparent: opts.transparent ?? false,
  });
  if (opts.emissive !== undefined) {
    m.emissive = new Color(opts.emissive);
  }
  if (opts.emissiveIntensity !== undefined) {
    m.emissiveIntensity = opts.emissiveIntensity;
  }
  return m;
}

/**
 * Convert an existing MeshStandardMaterial-like material to a toon material,
 * preserving its color + map. Returns a new material; caller should swap it in.
 */
export function toonifyMaterial(src: {
  color?: Color;
  map?: import('three').Texture | null;
  emissive?: Color;
  emissiveIntensity?: number;
  transparent?: boolean;
  name?: string;
}): MeshToonMaterial {
  const m = new MeshToonMaterial({
    color: src.color ? src.color.clone() : new Color('#ffffff'),
    map: src.map ?? null,
    gradientMap: getToonRamp(),
    transparent: src.transparent ?? false,
  });
  if (src.emissive) m.emissive = src.emissive.clone();
  if (src.emissiveIntensity !== undefined) m.emissiveIntensity = src.emissiveIntensity;
  if (src.name) m.name = src.name;
  return m;
}
